import { ReportFilters, ReportSortBy, ReportSortDir, useReportData } from '../../../contexts/ReportsContextProvider'
import styles from './OrganizationReport.module.css'
import { useUser } from '../../../contexts/userContext'
import { t } from 'i18next'
import { Button } from '../../../common/components/button/Button'
import { useCallback, useMemo, useState } from 'react'
import { getIcon } from '../../../common/components/icons/utils'
import classNames from 'classnames'
import { OrgReportUserData } from '../../../contexts/ReportsContextHelper'
import { PaginationSelectOption } from '../../../composites/GamesOverview/components/PaginationControls'
import { SingleValue } from 'react-select'
import { ReportFiltersForm } from './ReportFiltersForm'
import {
  DAY,
  EMPTY_GRAPH,
  getDayGraphData,
  getMonthGraphData,
  largeNumberFormat,
  NOW,
  OrgReportInsights,
  YEAR,
} from './ReportHelper'
import { BarGraph, createPDFfromHTML, SummaryDataBox } from './ReportCommon'
import reportStyles from './ReportCommon.module.css'
import { getOldUIRoutes } from '../../../routes'
import { CellElement, Table, TableRow } from './GenericTable'

export const OrganizationReport: React.FC = () => {
  const {
    orgSummaryData,
    orgDailyPlayers,
    pageData,
    currentPage,
    pageSize,
    allPageOptions,
    setPage,
    lastPage,
    next,
    prev,
    selectPageSize,
    loading,
    setFilters,
    setSort,
  } = useReportData()
  const { user } = useUser()
  const [insightText] = useState(OrgReportInsights()[Math.floor(Math.random() * OrgReportInsights.length)])
  const [showInsightBox, setShowInsightBox] = useState(true)
  const [playerGraphStartDate, setPlayerGraphStartDate] = useState(new Date(new Date(NOW() - YEAR).setDate(1)))
  const [playerGraphEndDate, setPlayerGraphEndDate] = useState(new Date(NOW()))
  const [activeSortCol, setActiveSortCol] = useState<ReportSortBy>()
  const [internalFilters, setInternalFilters] = useState<ReportFilters>({})
  const [hideShadows, setHideShadows] = useState(false)

  const handleDownloadPDF = useCallback(() => {
    setHideShadows(true)
    setTimeout(() => {
      createPDFfromHTML('PDF_DIV', 'Organisation_Report')
      setHideShadows(false)
    }, 25)
  }, [])

  const rangeSet = useMemo(() => {
    return internalFilters.createdAtFrom || internalFilters.createdAtUntil
  }, [internalFilters.createdAtFrom, internalFilters.createdAtUntil])

  const setFiltersInternal = useCallback(
    (filters: ReportFilters) => {
      setInternalFilters(filters)
      if (filters.createdAtFrom) {
        setPlayerGraphStartDate(new Date(filters.createdAtFrom))
      } else {
        setPlayerGraphStartDate(new Date(new Date(NOW() - YEAR).setDate(1)))
      }
      if (filters.createdAtUntil) {
        setPlayerGraphEndDate(new Date(filters.createdAtUntil))
        if (!filters.createdAtFrom) {
          setPlayerGraphStartDate(new Date(new Date(new Date(filters.createdAtUntil).getTime() - YEAR).setDate(1)))
        }
      } else {
        setPlayerGraphEndDate(new Date(NOW()))
      }
      setFilters(filters)
    },
    [setFilters],
  )

  const graphData = useMemo(() => {
    if (!orgDailyPlayers) {
      return EMPTY_GRAPH
    }
    const daysInRange = (playerGraphEndDate.getTime() - playerGraphStartDate.getTime()) / DAY

    if (daysInRange > 31) {
      return getMonthGraphData(orgDailyPlayers, playerGraphStartDate, playerGraphEndDate)
    } else {
      return getDayGraphData(orgDailyPlayers, playerGraphStartDate, playerGraphEndDate)
    }
  }, [playerGraphEndDate, playerGraphStartDate, orgDailyPlayers])

  const handleCloseInsightBox = useCallback(() => {
    setShowInsightBox(false)
  }, [])

  const handleSort = useCallback(
    (sortKey: string) => (direction: ReportSortDir | string) => {
      if (setSort) {
        setSort(sortKey as ReportSortBy, direction as ReportSortDir)
        setActiveSortCol(sortKey as ReportSortBy)
      }
    },
    [setSort],
  )

  const handleDownloadXLS = useCallback(() => {
    if (user)
      window.location.href = getOldUIRoutes.orgReportExcel(
        user?.activeBusiness.id,
        internalFilters.createdAtFrom,
        internalFilters.createdAtUntil,
      )
  }, [internalFilters.createdAtFrom, internalFilters.createdAtUntil, user])

  const onChangePageSize = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      selectPageSize(option!.value)
    },
    [selectPageSize],
  )

  const onChangeCurrentPage = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      setPage(option!.value)
    },
    [setPage],
  )

  const tableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []
    pageData?.forEach((dataRow) => {
      const row = dataRow as OrgReportUserData
      const tableRow: CellElement[] = [
        row.userName,
        row.numberOfGamesCreated,
        row.numberOfGamesPlayed,
        row.numberOfPlayersInGames,
        row.numberOfGamesCoOwned,
        row.numberOfGamesCoPlayed,
      ]
      rows.push(tableRow)
    })
    return rows
  }, [pageData])

  return (
    <div className={classNames(styles.container, hideShadows && styles.hideShadows)} id={'PDF_DIV'}>
      <div className={styles.headerRow}>
        <h1>{user?.activeBusiness.name}</h1>
        <Button style={{ marginLeft: 'auto' }} variant='outline-normal' onClick={handleDownloadPDF}>
          {t('reports.download_pdf', 'Export as PDF')}
        </Button>
        <Button variant='outline-normal' onClick={handleDownloadXLS}>
          {t('reports.download_excel', 'Export Excel')}
        </Button>
      </div>
      <ReportFiltersForm onChange={setFiltersInternal} />
      {showInsightBox && (
        <div className={styles.insightsBox}>
          <div className={styles.insightsLeft}>
            <div className={styles.insightTitle}>{t('reports.insight_title', 'Insights')}</div>
            <div className={styles.basicGreyText}>{insightText}</div>
          </div>
          <div className={styles.insightsRight} onClick={handleCloseInsightBox}>
            <span className={styles.closeIcon}>{getIcon('close')}</span>
          </div>
        </div>
      )}
      <div className={reportStyles.summaryDataBoxesRow}>
        <SummaryDataBox
          iconName='multipleUsers'
          value={orgSummaryData?.noOfUsers}
          label={t('reports.members', 'Members')}
          toolTipText={
            rangeSet
              ? t('reports.members_tooltip_range', 'Amount of members within date range')
              : t('reports.members_tooltip', 'Amount of members')
          }
        />
        <SummaryDataBox
          iconName='gameController'
          value={orgSummaryData?.noOfGames}
          label={t('reports.games_created', 'Games Created')}
          toolTipText={
            rangeSet
              ? t('reports.games_created_tooltip_range', 'Created games by members within date range')
              : t('reports.games_created_tooltip', 'Games created by members')
          }
        />
        <SummaryDataBox
          iconName='trophyOutline'
          value={orgSummaryData?.noOfPlayersInGames}
          label={t('reports.total_players', 'Total Players')}
          toolTipText={
            rangeSet
              ? t('reports.total_players_tooltip_range', 'Total players participating in these games')
              : t('reports.total_players_tooltip', 'Total players participating in these games')
          }
        />
        <SummaryDataBox
          iconName='play'
          value={orgSummaryData?.noOfPlayedGames}
          label={t('reports.games_played', 'Games Played')}
          toolTipText={
            rangeSet
              ? t('reports.games_played_tooltip_range', 'Games played within date range')
              : t('reports.games_played_tooltip', 'Games played')
          }
        />
      </div>
      <div>
        <h2>{t('reports.player_participation', 'Player Participation Trends')}</h2>
        <div className={styles.basicGreyText}>
          {rangeSet
            ? t(
                'reports.player_participation_info_range',
                "Track how many players have joined organization's games over time to identify engagement trends and peak activity periods. Includes also joining games that have been created outside date range. (Max two years from range start)",
              )
            : t(
                'reports.player_participation_info',
                "Track how many players have joined organization's games over time to identify engagement trends and peak activity periods.",
              )}
        </div>
      </div>
      <BarGraph graphData={graphData} />

      <h2>{t('reports.member_activity', 'Member Activity')}</h2>
      <div className={styles.activityBox}>
        <div className={classNames(styles.activityItem, styles.activityItemPink, styles.activityText)}>
          <span className={styles.activityIconPink}>{getIcon('multipleUsers')}</span>
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'reports.activity_head_text1',
                'Your members have been busy! A total of <span class="%{class}">%{users_count} unique game creators</span> have crafted <span class="%{class}">%{games_count} games</span> and actively used <span class="%{class}">%{played_count} games</span>, turning ideas into engaging learning experiences!',
                {
                  users_count: orgSummaryData?.noOfUsers,
                  games_count: orgSummaryData?.noOfGames,
                  played_count: orgSummaryData?.noOfPlayedGames,
                  class: styles.activityHighlightTextPink,
                },
              ),
            }}
          />
        </div>
        <div className={classNames(styles.activityItem, styles.activityText)}>
          <span className={styles.activityIcon}>{getIcon('gameController')}</span>
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'reports.activity_head_text2',
                'Games under your organisation have been played by <span class="%{class}">%{player_count} eager minds</span>. Together, they’ve interacted with <span class="%{class}">%{tasks_count} tasks</span> and collected a mind-blowing <span class="%{class}">%{points_count} points!</span>',
                {
                  tasks_count: orgSummaryData?.noOfTasks,
                  player_count: orgSummaryData?.noOfPlayersInGames,
                  points_count: largeNumberFormat(orgSummaryData?.pointsCount),
                  class: styles.activityHighlightText,
                },
              ),
            }}
          />
        </div>
      </div>
      <Table
        isSortable
        hasPagination
        headerWidths={['280px', '13%', '13%', '13%', '13%', '13%']}
        headerLabels={[
          t('reports.table.name_or_email', 'Name/email'),
          t('reports.table.games_created', 'Games created'),
          t('reports.table.games_played', 'Games played'),
          t('reports.table.players_in_games', 'Players in games'),
          t('reports.table.co_owned', 'Co-owned'),
          t('reports.table.co_played', 'Co-played'),
        ]}
        headerSortTags={[
          ReportSortBy.NAME,
          ReportSortBy.GAMES_CREATED,
          ReportSortBy.GAMES_PLAYED,
          ReportSortBy.PLAYERS_IN_GAMES,
          ReportSortBy.GAMES_CO_OWNED,
          ReportSortBy.GAMES_CO_PLAYED,
        ]}
        onSort={handleSort}
        activeSortCol={activeSortCol}
        rows={tableRowData}
        currentPage={currentPage}
        pageSize={pageSize}
        lastPage={lastPage}
        allPageOptions={allPageOptions}
        onClickNext={next}
        onClickPrev={prev}
        onChangePageSize={onChangePageSize}
        onChangeCurrentPage={onChangeCurrentPage}
        itemsPerPageLabel={t('reports.pagination.users_per_page', 'users per page')}
        loading={loading}
      />
    </div>
  )
}
